import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { logger } from "@/lib/logger";
import { api, apiError } from "@/lib/api";
import { LoadingState, ErrorState } from "@/components/States";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Sparkles, KeyRound, Volume2, Youtube, Image as ImageIcon, Loader2,
  CheckCircle2, XCircle, PlugZap, Trash2, Save, LinkIcon, Unplug, Copy,
  Plus, Pencil, Puzzle, ServerCog,
} from "lucide-react";

const ICONS = {
  emergent: Sparkles, openai: KeyRound, elevenlabs: Volume2,
  openai_tts: Volume2, youtube: Youtube, pexels: ImageIcon,
};

function StatusPill({ state }) {
  if (state.kind === "planned")
    return <span className="text-xs px-2.5 py-1 rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/30" data-testid="status-planned">Planned · {state.phase}</span>;
  if (state.connected)
    return <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/30" data-testid="status-connected"><CheckCircle2 className="w-3 h-3" /> {state.kind === "builtin" ? "Connected (built-in)" : "Connected"}</span>;
  return <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-slate-700/40 text-slate-400 border border-slate-600/40" data-testid="status-disconnected"><XCircle className="w-3 h-3" /> Disconnected</span>;
}

function Card({ state, onRefresh }) {
  const Icon = ICONS[state.provider] || PlugZap;
  const [keyVal, setKeyVal] = useState("");
  const [busy, setBusy] = useState(false);
  const planned = state.kind === "planned";
  const builtin = state.kind === "builtin";
  const secret = state.kind === "secret";

  const saveKey = async () => {
    if (!keyVal.trim()) { toast.error("Enter an API key"); return; }
    setBusy(true);
    try { await api.put(`/integrations/${state.provider}/key`, { api_key: keyVal.trim() }); setKeyVal(""); toast.success("Key saved (encrypted)"); await onRefresh(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const test = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/integrations/${state.provider}/test`);
      const r = data.last_test_result;
      r?.ok ? toast.success(r.message) : toast.error(r?.message || "Test failed");
      await onRefresh();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const remove = async () => {
    setBusy(true);
    try { await api.delete(`/integrations/${state.provider}/key`); toast.success("Key removed"); await onRefresh(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className={`bg-[#111827] border rounded-xl p-5 flex flex-col ${planned ? "border-slate-800/60 opacity-80" : "border-slate-800/80"}`} data-testid={`integration-card-${state.provider}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-slate-800/70 border border-slate-700/60 flex items-center justify-center flex-none">
            <Icon className="w-5 h-5 text-violet-300" />
          </div>
          <div>
            <h3 className="font-display font-semibold text-slate-100 leading-tight">{state.name}</h3>
            <p className="text-xs text-slate-500">{state.functions.join(" · ")}</p>
          </div>
        </div>
        <StatusPill state={state} />
      </div>

      {builtin && state.provider === "emergent" && (
        <p className="text-sm text-slate-400 mb-4">Uses the Emergent universal key configured on the server. No setup needed.</p>
      )}

      {state.note && (
        <p className="text-xs text-slate-400 mb-4 leading-relaxed" data-testid={`note-${state.provider}`}>{state.note}</p>
      )}

      {secret && (
        <div className="mb-3">
          {state.masked_key && <p className="text-xs text-slate-500 mb-2">Stored key: <span className="font-mono text-slate-300" data-testid={`masked-key-${state.provider}`}>{state.masked_key}</span></p>}
          <Input type="password" placeholder={state.masked_key ? "Replace key…" : (state.key_hint || "Paste API key…")} value={keyVal}
            onChange={(e) => setKeyVal(e.target.value)} data-testid={`key-input-${state.provider}`}
            className="bg-slate-900 border-slate-800 text-slate-100 focus-visible:ring-violet-500/30 font-mono text-sm" />
        </div>
      )}

      {state.last_test_result && (
        <div className={`text-xs rounded-lg px-3 py-2 mb-3 ${state.last_test_result.ok ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/25" : "bg-rose-500/10 text-rose-300 border border-rose-500/25"}`} data-testid={`test-result-${state.provider}`}>
          {state.last_test_result.message}
        </div>
      )}
      {state.models?.length > 0 && (
        <p className="text-xs text-slate-500 mb-3" data-testid={`models-${state.provider}`}>Models: {state.models.slice(0, 4).join(", ")}{state.models.length > 4 ? ` +${state.models.length - 4}` : ""}</p>
      )}
      {state.last_tested_at && <p className="text-[11px] text-slate-600 mb-3">Last tested {new Date(state.last_tested_at).toLocaleString()}</p>}

      <div className="mt-auto flex items-center gap-2">
        {planned ? (
          <Button disabled size="sm" className="w-full bg-slate-800 text-slate-500 cursor-not-allowed border border-slate-700/50" data-testid={`disabled-${state.provider}`}>Coming in {state.phase || "a later phase"}</Button>
        ) : (
          <>
            {secret && <Button onClick={saveKey} disabled={busy} size="sm" className="bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700" data-testid={`save-key-${state.provider}`}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> Save</>}</Button>}
            {state.testable && <Button onClick={test} disabled={busy} size="sm" className="bg-violet-600 hover:bg-violet-500 text-white" data-testid={`test-${state.provider}`}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><PlugZap className="w-4 h-4 mr-1.5" /> Test connection</>}</Button>}
            {!state.testable && !secret && (
              <span className="text-xs text-slate-500 py-1" data-testid={`no-test-${state.provider}`}>
                {state.kind === "library" ? (state.install_note || "Optional code library — no API key required")
                  : state.kind === "derived" ? "No direct test — verify via its parent key"
                  : "No connection test available"}
              </span>
            )}
            {secret && state.masked_key && <Button onClick={remove} disabled={busy} size="sm" variant="ghost" className="text-rose-400 hover:bg-slate-800 ml-auto" data-testid={`remove-key-${state.provider}`}><Trash2 className="w-4 h-4" /></Button>}
          </>
        )}
      </div>
    </div>
  );
}

const inputCls = "bg-slate-900 border-slate-800 text-slate-100 focus-visible:ring-violet-500/30 font-mono text-sm";

function YouTubeCard() {
  const [state, setState] = useState(null);
  const [bindings, setBindings] = useState([]);
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/youtube/status"); setState(data);
      const { data: aud } = await api.get("/youtube/connections/audit"); setBindings(aud.bindings || []);
    } catch (e) { logger.error("youtube card load failed", e); }
  };
  useEffect(() => { load(); }, []);

  const saveCreds = async () => {
    if (!clientId.trim() || !clientSecret.trim()) { toast.error("Enter Client ID and Client Secret"); return; }
    setBusy(true);
    try { await api.put("/youtube/credentials", { client_id: clientId.trim(), client_secret: clientSecret.trim() }); setClientId(""); setClientSecret(""); toast.success("Credentials saved (encrypted)"); await load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const deleteCreds = async () => {
    setBusy(true);
    try { await api.delete("/youtube/credentials"); toast.success("Credentials removed"); await load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const copyRedirect = () => { navigator.clipboard?.writeText(state.redirect_uri); toast.success("Redirect URI copied"); };

  if (!state) return <div className="bg-[#111827] border border-slate-800/80 rounded-xl p-5"><Loader2 className="w-4 h-4 animate-spin text-violet-300" /></div>;

  const connectedCount = bindings.filter((b) => b.verified).length;

  return (
    <div className="bg-[#111827] border border-slate-800/80 rounded-xl p-5 flex flex-col md:col-span-2 lg:col-span-3" data-testid="integration-card-youtube">
      <div className="flex items-start justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-slate-800/70 border border-slate-700/60 flex items-center justify-center flex-none"><Youtube className="w-5 h-5 text-rose-400" /></div>
          <div className="min-w-0">
            <h3 className="font-display font-semibold text-slate-100 leading-tight">YouTube (Google OAuth client)</h3>
            <p className="text-xs text-slate-500">Workspace client config · each channel holds its OWN grant &amp; binding</p>
          </div>
        </div>
        {state.configured
          ? <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/30" data-testid="youtube-client-configured"><CheckCircle2 className="w-3 h-3" /> Client configured · {connectedCount} channel{connectedCount === 1 ? "" : "s"} connected</span>
          : <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-slate-700/40 text-slate-400 border border-slate-600/40" data-testid="youtube-client-unconfigured"><XCircle className="w-3 h-3" /> Client not configured</span>}
      </div>

      <div className="text-xs text-slate-400 bg-slate-900/50 border border-slate-800 rounded-lg p-3 mb-4">
        <p className="mb-1.5 text-slate-300 font-medium">Google Cloud setup checklist (HUMAN_REQUIRED)</p>
        <ol className="list-decimal pl-4 space-y-1">
          <li>Create/select a project at console.cloud.google.com and enable <b>YouTube Data API v3</b>.</li>
          <li>Configure the OAuth consent screen (External); add your Google account as a Test user.</li>
          <li>Create an <b>OAuth Client ID</b> of type <b>Web application</b>.</li>
          <li>Add this exact <b>Authorized redirect URI</b>:</li>
        </ol>
        <div className="flex items-center gap-2 mt-2">
          <code className="flex-1 truncate bg-slate-950 border border-slate-800 rounded px-2 py-1 text-slate-300" data-testid="youtube-redirect-uri">{state.redirect_uri}</code>
          <button onClick={copyRedirect} className="text-slate-400 hover:text-violet-300" data-testid="copy-redirect"><Copy className="w-4 h-4" /></button>
        </div>
        <p className="mt-2 text-slate-400">This is the workspace <b>OAuth client</b> only. Connecting a channel to a specific YouTube channel happens on each channel's page (Google shows the account/channel chooser) — the client config here never changes when you connect a channel.</p>
      </div>

      {state.configured ? (
        <div className="text-xs text-slate-500 mb-3">Client ID: <span className="font-mono text-slate-300" data-testid="youtube-client-id-masked">{state.client_id_masked}</span></div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <div><Label className="text-slate-300 text-xs mb-1 block">Client ID</Label><Input value={clientId} onChange={(e) => setClientId(e.target.value)} placeholder="xxxxx.apps.googleusercontent.com" className={inputCls} data-testid="youtube-client-id" /></div>
          <div><Label className="text-slate-300 text-xs mb-1 block">Client Secret</Label><Input type="password" value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} placeholder="GOCSPX-…" className={inputCls} data-testid="youtube-client-secret" /></div>
        </div>
      )}

      <div className="mb-3" data-testid="youtube-bindings-list">
        <p className="text-[11px] uppercase tracking-wide text-slate-600 mb-1.5">Channel bindings (each an independent grant)</p>
        {bindings.length === 0 ? (
          <p className="text-xs text-slate-500">No channel is connected yet. Open a channel and use “Connect YouTube” — Google will show the account/channel chooser.</p>
        ) : (
          <div className="space-y-1.5">
            {bindings.map((b) => (
              <div key={b.id} className={`rounded-lg border px-3 py-2 flex items-center justify-between gap-3 flex-wrap ${b.verified ? "border-emerald-500/25 bg-emerald-500/5" : "border-amber-500/30 bg-amber-500/5"}`} data-testid={`yt-binding-${b.id}`}>
                <div className="min-w-0">
                  <p className="text-sm text-slate-200 break-words">{b.app_channel_name || <span className="text-amber-400">unbound / legacy</span>} <span className="text-slate-500">→</span> {b.youtube_channel_title || "(unknown)"}</p>
                  <p className="text-[11px] text-slate-500 font-mono break-all">{b.youtube_channel_id}</p>
                </div>
                <span className={`text-[11px] flex-none ${b.verified ? "text-emerald-400" : "text-amber-400"}`}>{b.verified ? "connected" : b.health}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-auto flex flex-wrap items-center gap-2">
        {!state.configured && <Button onClick={saveCreds} disabled={busy} size="sm" className="bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700" data-testid="youtube-save-creds">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> Save credentials</>}</Button>}
        {state.configured && <Button onClick={deleteCreds} disabled={busy} size="sm" variant="ghost" className="text-rose-400 hover:bg-slate-800 ml-auto" data-testid="youtube-delete-creds"><Trash2 className="w-4 h-4 mr-1.5" /> Remove client config</Button>}
      </div>
    </div>
  );
}

const ADAPTER_META = {
  "agent-reach": { label: "Agent-Reach", desc: "Reads public pages found by Tavily for Idea Radar. No API key needed. Social tools require separate setup.", role: "research-agent" },
  github: { label: "GitHub", desc: "Kod araşdırması: açıq depo axtarışı və metadata (yalnız oxu). Public axtarış token OLMADAN işləyir (rate-limited); daha yüksək limit üçün Personal Access Token (fine-grained və ya classic) əlavə edin. — Read-only repo search & metadata for research. Public search works without a token; add a Personal Access Token for higher limits.", role: "research-code" },
  tavily: { label: "Tavily Search", desc: "Real live web search for Research & Ideas.", role: "research-search" },
  langfuse: { label: "Langfuse", desc: "LLM observability / tracing.", role: "observability" },
  elevenlabs: { label: "ElevenLabs", desc: "Voice: TTS + alignment + transcription.", role: "audio" },
};
const ELEVEN_CAPS = [
  { key: "multilingual-v2", label: "Multilingual v2 (TTS)" },
  { key: "multilingual-v3", label: "v3 (TTS)" },
  { key: "forced-alignment", label: "Forced Alignment" },
  { key: "scribe-v2", label: "Scribe v2 (transcription)" },
];

function AdapterCard({ a, onRefresh }) {
  const meta = ADAPTER_META[a.provider] || { label: a.provider, desc: "", role: "" };
  const [vals, setVals] = useState({});
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setVals((s) => ({ ...s, [k]: v }));
  const save = async () => {
    setBusy(true);
    try { await api.put("/adapters/config", { provider: a.provider, values: vals }); toast.success("Saved (masked)"); setVals({}); await onRefresh(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const test = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/adapters/${a.provider}/test`);
      data.ok ? toast.success(`${meta.label} connected (${data.latency_ms}ms)`) : toast.error(`Test failed: ${(data.error || "").slice(0, 140)}`);
      await onRefresh();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const stateCls = a.state === "available" ? "text-emerald-400 bg-emerald-500/10 border-emerald-500/30"
    : a.state === "failed" ? "text-rose-300 bg-rose-500/10 border-rose-500/30"
      : "text-sky-300 bg-sky-500/10 border-sky-500/30";
  return (
    <div className="bg-[#111827] border border-slate-800/80 rounded-xl p-5" data-testid={`adapter-card-${a.provider}`}>
      <div className="flex items-start justify-between mb-3">
        <div><h3 className="font-display font-semibold text-slate-100">{meta.label}</h3><p className="text-xs text-slate-500">{meta.desc}</p></div>
        <span className={`text-xs px-2.5 py-1 rounded-full border ${stateCls}`} data-testid={`adapter-state-${a.provider}`}>{a.state}</span>
      </div>
      <div className="space-y-2 mb-3">
        {a.key_fields.map((f) => (
          <div key={f}>
            <Label className="text-slate-400 text-xs mb-1 block">{f}{a.fields[f]?.set && <span className="ml-2 text-slate-600 font-mono" data-testid={`adapter-masked-${a.provider}-${f}`}>saved: {a.fields[f].masked}</span>}</Label>
            <Input type={f === "host" ? "text" : "password"} className={`${inputCls} font-mono text-xs`} placeholder={f === "host" ? "https://cloud.langfuse.com" : "•••"}
              value={vals[f] || ""} onChange={(e) => set(f, e.target.value)} data-testid={`adapter-field-${a.provider}-${f}`} />
          </div>
        ))}
      </div>
      {a.provider === "elevenlabs" && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {ELEVEN_CAPS.map((c) => (
            <span key={c.key} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-400 border border-slate-700" data-testid={`adapter-cap-${c.key}`}>{c.label} · {a.state === "available" ? "connected" : "key-required"}</span>
          ))}
        </div>
      )}
      {a.last_detail && <p className="text-xs text-slate-400 mb-2" data-testid={`adapter-detail-${a.provider}`}>{a.last_detail}</p>}
      {a.last_error && <p className="text-[11px] text-rose-400/80 mb-2 break-all" data-testid={`adapter-error-${a.provider}`}>{a.last_error}</p>}
      <div className="flex items-center gap-2">
        {a.key_fields.length > 0 && <Button onClick={save} disabled={busy} size="sm" className="bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700" data-testid={`adapter-save-${a.provider}`}>Save key</Button>}
        <Button onClick={test} disabled={busy} size="sm" className="bg-violet-600 hover:bg-violet-500 text-white" data-testid={`adapter-test-${a.provider}`}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><PlugZap className="w-4 h-4 mr-1.5" /> Test connection</>}</Button>
      </div>
    </div>
  );
}

function AdaptersSection() {
  const [list, setList] = useState(null);
  const load = async () => { try { const { data } = await api.get("/adapters"); setList(data); } catch (e) { logger.error("adapters load failed", e); } };
  useEffect(() => { load(); }, []);
  if (!list) return null;
  return (
    <div className="mt-10" data-testid="adapters-section">
      <h2 className="text-lg font-display font-bold text-slate-100 mb-1">Provider adapters</h2>
      <p className="text-sm text-slate-400 mb-5">Test connections with a real request. Providers with key fields require saved credentials; Agent-Reach needs no API key.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {list.map((a) => <AdapterCard key={a.provider} a={a} onRefresh={load} />)}
      </div>
    </div>
  );
}

const CATEGORIES = ["analytics", "storage", "email", "cms", "payment", "other"];
const SCOPE_LINK = "/YOUTUBE_SETUP.md";

function YouTubeReadinessPanel() {
  const [r, setR] = useState(null);
  const [sm, setSm] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try {
      const [a, b] = await Promise.all([api.get("/youtube/readiness"), api.get("/youtube/scope-map")]);
      setR(a.data); setSm(b.data);
    } catch (e) { logger.error("youtube readiness load failed", e); /* panel stays hidden if unreachable */ }
  };
  useEffect(() => { load(); }, []);
  const testUpload = async () => {
    setBusy(true);
    try { await api.post("/youtube/test-upload"); toast.success("Test upload succeeded"); }
    catch (e) { toast.message(apiError(e)); }
    finally { setBusy(false); load(); }
  };
  if (!r) return null;
  const DOT_CLS = { green: "bg-emerald-400", amber: "bg-amber-400", red: "bg-rose-400" };
  const dot = (st) => DOT_CLS[st] || DOT_CLS.red;
  const overallCls = r.overall === "green" ? "text-emerald-400 border-emerald-500/30 bg-emerald-500/10"
    : r.overall === "amber" ? "text-amber-300 border-amber-500/30 bg-amber-500/10"
      : "text-rose-300 border-rose-500/30 bg-rose-500/10";
  return (
    <div className="mt-10 bg-[#111827] border border-slate-800/80 rounded-xl p-6" data-testid="youtube-readiness">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h2 className="text-lg font-display font-bold text-slate-100 flex items-center gap-2"><Youtube className="w-5 h-5 text-rose-400" /> YouTube readiness</h2>
        <span className={`text-xs px-2.5 py-1 rounded-full border ${overallCls}`} data-testid="readiness-overall">{r.overall.toUpperCase()}</span>
      </div>
      <div className="space-y-2 mb-5">
        {r.checklist.map((c) => (
          <div key={c.key} className="flex items-start gap-3 bg-slate-900/50 border border-slate-800 rounded-lg px-4 py-3" data-testid={`readiness-item-${c.key}`}>
            <span className={`w-2.5 h-2.5 rounded-full mt-1.5 flex-none ${dot(c.status)}`} data-testid={`readiness-dot-${c.key}`} />
            <div>
              <p className="text-sm text-slate-200">{c.label}</p>
              <p className="text-xs text-slate-500 mt-0.5">{c.detail}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <Button onClick={testUpload} disabled={busy || !r.real_connection} size="sm"
          className="bg-violet-600 hover:bg-violet-500 text-white disabled:opacity-50" data-testid="youtube-test-upload">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><PlugZap className="w-4 h-4 mr-1.5" /> Test private upload</>}
        </Button>
        {!r.real_connection && <span className="text-xs text-slate-500">Connect a channel first — this runs a REAL upload (HUMAN_REQUIRED).</span>}
      </div>
      {sm && (
        <div className="border-t border-slate-800 pt-4">
          <p className="text-xs uppercase tracking-wide text-slate-600 mb-2">Scope → feature mapping</p>
          <div className="space-y-1.5" data-testid="scope-map">
            {sm.scopes.map((s) => (
              <div key={s.scope} className="text-xs text-slate-400 flex flex-col sm:flex-row sm:gap-3">
                <span className="font-mono text-violet-300 sm:w-52 flex-none truncate">{s.scope.replace("https://www.googleapis.com/auth/", "")}</span>
                <span>{s.features.join(", ")}</span>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-amber-300/80 mt-3">{sm.note}</p>
          <p className="text-xs text-slate-500 mt-2">The app does not create YouTube channels — create one on YouTube first, then connect. Full setup guide: <span className="text-violet-300">/app/YOUTUBE_SETUP.md</span></p>
        </div>
      )}
    </div>
  );
}

const AUTH_TYPES = [
  { v: "none", label: "None" },
  { v: "api_key", label: "API key (header)" },
  { v: "bearer", label: "Bearer token" },
  { v: "custom_header", label: "Custom header" },
];

function CustomStatusPill({ status }) {
  if (status === "connected")
    return <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/30" data-testid="custom-status-connected"><CheckCircle2 className="w-3 h-3" /> Connected</span>;
  if (status === "failed")
    return <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-rose-500/10 text-rose-300 border border-rose-500/30" data-testid="custom-status-failed"><XCircle className="w-3 h-3" /> Failed</span>;
  return <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-slate-700/40 text-slate-400 border border-slate-600/40" data-testid="custom-status-untested">Untested</span>;
}

function CustomDialog({ initial, onDone }) {
  const [open, setOpen] = useState(false);
  const blank = { name: "", category: "other", base_url: "", auth_type: "none", credential: "",
    header_name: "", notes: "", method: "GET", path: "", expected_status: 200 };
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    if (open) setF(initial ? {
      name: initial.name, category: initial.category, base_url: initial.base_url || "",
      auth_type: initial.auth_type, credential: "", header_name: initial.header_name || "",
      notes: initial.notes || "", method: initial.test_config?.method || "GET",
      path: initial.test_config?.path || "", expected_status: initial.test_config?.expected_status || 200,
    } : blank);
  }, [open]); // eslint-disable-line

  const save = async () => {
    if (!f.name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      const body = { name: f.name.trim(), category: f.category, base_url: f.base_url.trim() || null,
        auth_type: f.auth_type, header_name: f.header_name.trim() || null, notes: f.notes.trim(),
        test_config: { method: f.method, path: f.path.trim(), expected_status: Number(f.expected_status) || 200 } };
      if (f.auth_type !== "none" && f.credential.trim()) body.credential = f.credential.trim();
      if (initial?.id) await api.put(`/integrations/custom/${initial.id}`, body);
      else await api.post("/integrations/custom", body);
      toast.success(initial?.id ? "Integration updated" : "Custom integration added");
      setOpen(false); onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {initial
          ? <Button size="sm" variant="ghost" className="text-slate-300 hover:bg-slate-800" data-testid={`custom-edit-${initial.id}`}><Pencil className="w-4 h-4" /></Button>
          : <Button className="bg-violet-600 hover:bg-violet-500 text-white" data-testid="custom-add-btn"><Plus className="w-4 h-4 mr-1.5" /> Add integration</Button>}
      </DialogTrigger>
      <DialogContent className="bg-[#111827] border-slate-800 text-slate-100 max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{initial ? "Edit custom integration" : "Add custom integration"}</DialogTitle></DialogHeader>
        <div className="text-xs text-slate-400 bg-slate-900/60 border border-slate-800 rounded-lg p-3 flex items-start gap-2">
          <ServerCog className="w-4 h-4 mt-0.5 flex-none text-violet-300" />
          <span>Stored credentials + connectivity check. To make an integration DO work in production flows, connect it as an MCP server in MCP Connections.</span>
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-slate-300 text-sm mb-1 block">Name</Label><Input className={inputCls} value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="custom-name" /></div>
            <div><Label className="text-slate-300 text-sm mb-1 block">Category</Label>
              <Select value={f.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger className={inputCls} data-testid="custom-category"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#111827] border-slate-700 text-slate-200">{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label className="text-slate-300 text-sm mb-1 block">Base URL <span className="text-slate-600">(https://…)</span></Label><Input className={inputCls} placeholder="https://api.example.com" value={f.base_url} onChange={(e) => set("base_url", e.target.value)} data-testid="custom-base-url" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-slate-300 text-sm mb-1 block">Auth type</Label>
              <Select value={f.auth_type} onValueChange={(v) => set("auth_type", v)}>
                <SelectTrigger className={inputCls} data-testid="custom-auth-type"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#111827] border-slate-700 text-slate-200">{AUTH_TYPES.map((a) => <SelectItem key={a.v} value={a.v}>{a.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {(f.auth_type === "api_key" || f.auth_type === "custom_header") && (
              <div><Label className="text-slate-300 text-sm mb-1 block">Header name</Label><Input className={inputCls} placeholder="X-API-Key" value={f.header_name} onChange={(e) => set("header_name", e.target.value)} data-testid="custom-header-name" /></div>
            )}
          </div>
          {f.auth_type !== "none" && (
            <div><Label className="text-slate-300 text-sm mb-1 block">Credential {initial && <span className="text-slate-600">(leave blank to keep current)</span>}</Label><Input type="password" className={`${inputCls} font-mono`} placeholder="token / key…" value={f.credential} onChange={(e) => set("credential", e.target.value)} data-testid="custom-credential" /></div>
          )}
          <div className="grid grid-cols-3 gap-3">
            <div><Label className="text-slate-300 text-sm mb-1 block">Test method</Label>
              <Select value={f.method} onValueChange={(v) => set("method", v)}>
                <SelectTrigger className={inputCls} data-testid="custom-method"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#111827] border-slate-700 text-slate-200">{["GET", "HEAD", "POST"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-slate-300 text-sm mb-1 block">Test path</Label><Input className={inputCls} placeholder="/status" value={f.path} onChange={(e) => set("path", e.target.value)} data-testid="custom-test-path" /></div>
            <div><Label className="text-slate-300 text-sm mb-1 block">Expect status</Label><Input type="number" className={inputCls} value={f.expected_status} onChange={(e) => set("expected_status", e.target.value)} data-testid="custom-expected-status" /></div>
          </div>
          <div><Label className="text-slate-300 text-sm mb-1 block">Notes</Label><Textarea className={inputCls} value={f.notes} onChange={(e) => set("notes", e.target.value)} data-testid="custom-notes" /></div>
        </div>
        <DialogFooter><Button onClick={save} disabled={busy} className="bg-violet-600 hover:bg-violet-500 text-white" data-testid="custom-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CustomCard({ item, onRefresh }) {
  const [busy, setBusy] = useState(false);
  const test = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/integrations/custom/${item.id}/test`);
      data.ok ? toast.success(data.test_message) : toast.error(data.test_message || "Test failed");
      await onRefresh();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const remove = async () => {
    setBusy(true);
    try { await api.delete(`/integrations/custom/${item.id}`); toast.success("Integration removed"); await onRefresh(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="bg-[#111827] border border-violet-500/30 rounded-xl p-5 flex flex-col" data-testid={`custom-card-${item.id}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-slate-800/70 border border-slate-700/60 flex items-center justify-center flex-none"><Puzzle className="w-5 h-5 text-violet-300" /></div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-display font-semibold text-slate-100 leading-tight">{item.name}</h3>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-300 border border-violet-500/30" data-testid={`custom-badge-${item.id}`}>Custom</span>
            </div>
            <p className="text-xs text-slate-500">{item.category}{item.base_url ? ` · ${item.base_url}` : ""}</p>
          </div>
        </div>
        <CustomStatusPill status={item.status} />
      </div>

      {item.has_credential && <p className="text-xs text-slate-500 mb-2">Credential: <span className="font-mono text-slate-300" data-testid={`custom-masked-${item.id}`}>{item.masked_credential}</span> <span className="text-slate-600">({item.auth_type})</span></p>}
      {item.notes && <p className="text-xs text-slate-400 mb-2">{item.notes}</p>}

      <div className="text-[11px] text-slate-500 bg-slate-900/50 border border-slate-800 rounded-lg px-3 py-2 mb-3 flex items-start gap-2" data-testid={`custom-scope-${item.id}`}>
        <ServerCog className="w-3.5 h-3.5 mt-0.5 flex-none text-violet-300/80" />
        <span>{item.scope_label}</span>
      </div>

      {item.last_error && item.status === "failed" && <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/25 rounded-lg px-3 py-2 mb-3" data-testid={`custom-error-${item.id}`}>{item.last_error}</div>}
      {item.last_tested_at && <p className="text-[11px] text-slate-600 mb-3">Last tested {new Date(item.last_tested_at).toLocaleString()}</p>}

      <div className="mt-auto flex items-center gap-2">
        <Button onClick={test} disabled={busy} size="sm" className="bg-violet-600 hover:bg-violet-500 text-white" data-testid={`custom-test-${item.id}`}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><PlugZap className="w-4 h-4 mr-1.5" /> Test connection</>}</Button>
        <CustomDialog initial={item} onDone={onRefresh} />
        <Button onClick={remove} disabled={busy} size="sm" variant="ghost" className="text-rose-400 hover:bg-slate-800 ml-auto" data-testid={`custom-delete-${item.id}`}><Trash2 className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}

function BindingAudit() {
  const [data, setData] = useState(null);
  const load = async () => { try { const { data } = await api.get("/youtube/connections/audit"); setData(data); } catch (e) { logger.error("binding audit load failed", e); } };
  useEffect(() => { load(); }, []);
  const confirm = async (id, verified) => {
    try { await api.post(`/youtube/connections/${id}/confirm`, { verified }); toast.success(verified ? "Binding confirmed" : "Binding un-confirmed"); await load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  if (!data || data.count === 0) return null;
  return (
    <div className="mt-10 bg-[#111827] border border-slate-800/80 rounded-xl p-6" data-testid="binding-audit">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
        <h2 className="text-lg font-display font-bold text-slate-100 flex items-center gap-2"><Youtube className="w-5 h-5 text-rose-400" /> YouTube binding audit</h2>
        {data.unverified_count > 0
          ? <span className="text-xs px-2.5 py-1 rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/30" data-testid="binding-audit-unverified">{data.unverified_count} need review — publishing blocked</span>
          : <span className="text-xs px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/30">All bindings verified</span>}
      </div>
      <p className="text-sm text-slate-400 mb-4">{data.note}</p>
      <div className="space-y-2">
        {data.bindings.map((b) => (
          <div key={b.id} className={`rounded-lg border px-4 py-3 ${b.verified ? "border-emerald-500/25 bg-emerald-500/5" : "border-amber-500/30 bg-amber-500/5"}`} data-testid={`binding-row-${b.id}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <p className="text-sm text-slate-200">{b.youtube_channel_title || "(unknown YouTube channel)"} <span className="text-slate-500 font-mono text-xs">{b.youtube_channel_id}</span></p>
                <p className="text-xs text-slate-500">Profile: {b.app_channel_name || <span className="text-amber-400">unbound / legacy</span>} · health {b.health}</p>
              </div>
              <div className="flex items-center gap-2">
                {b.verified
                  ? <span className="inline-flex items-center gap-1 text-xs text-emerald-400" data-testid={`binding-verified-${b.id}`}><CheckCircle2 className="w-3.5 h-3.5" /> Verified · publishing allowed</span>
                  : <Button size="sm" onClick={() => confirm(b.id, true)} className="bg-violet-600 hover:bg-violet-500 text-white" data-testid={`binding-confirm-${b.id}`}>Confirm this binding</Button>}
              </div>
            </div>
            {b.needs_owner_review && b.review_reasons.length > 0 && (
              <ul className="mt-2 text-[11px] text-amber-300/90 list-disc pl-4 space-y-0.5" data-testid={`binding-reasons-${b.id}`}>
                {b.review_reasons.map((r) => <li key={r}>{r}</li>)}
              </ul>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Integrations() {
  const [items, setItems] = useState(null);
  const [custom, setCustom] = useState(null);
  const [error, setError] = useState(null);
  const [sp, setSp] = useSearchParams();

  const load = async () => {
    setError(null);
    try {
      const [prov, cust] = await Promise.all([api.get("/integrations"), api.get("/integrations/custom")]);
      setItems(prov.data);
      setCustom(cust.data.items);
    } catch (e) { setError(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const yt = sp.get("youtube");
    if (yt === "connected") { toast.success("YouTube connected"); sp.delete("youtube"); setSp(sp, { replace: true }); }
    else if (yt === "error") { toast.error(`YouTube connection failed${sp.get("reason") ? `: ${sp.get("reason")}` : ""}`); sp.delete("youtube"); sp.delete("reason"); setSp(sp, { replace: true }); }
  }, [sp, setSp]);

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!items) return <LoadingState label="Loading integrations…" />;

  const others = items.filter((i) => i.provider !== "youtube");
  const mediaStack = others.filter((i) => i.group === "media_stack");
  const general = others.filter((i) => i.group !== "media_stack");

  return (
    <div className="animate-in-up">
      <div className="mb-8 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[11px] font-semibold tracking-widest uppercase text-violet-400 mb-1">Workspace</p>
          <h1 className="text-3xl sm:text-4xl font-display font-extrabold tracking-tight text-slate-50">Integrations</h1>
          <p className="text-sm text-slate-400 mt-2">Connect providers. Keys are encrypted at rest and never shown in full. Test connection makes a real API call.</p>
        </div>
        <CustomDialog onDone={load} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        <YouTubeCard />
        {general.map((it) => <Card key={it.provider} state={it} onRefresh={load} />)}
      </div>

      {mediaStack.length > 0 && (
        <div className="mt-10" data-testid="media-stack-section">
          <div className="flex items-center gap-2 mb-1">
            <ImageIcon className="w-4 h-4 text-violet-300" />
            <h2 className="text-lg font-display font-bold text-slate-100">Media &amp; Generation Stack</h2>
          </div>
          <p className="text-sm text-slate-400 mb-5 max-w-3xl">Image, video, transcription and render capabilities. Some are derived from a parent BYOK key (no separate key), some are built-in, and some are honest reserved slots — a Test button appears only where a real test exists.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {mediaStack.map((it) => <Card key={it.provider} state={it} onRefresh={load} />)}
          </div>
        </div>
      )}

      <YouTubeReadinessPanel />

      <BindingAudit />

      <AdaptersSection />

      <div className="mt-10">
        <div className="flex items-center gap-2 mb-2">
          <Puzzle className="w-4 h-4 text-violet-300" />
          <h2 className="text-lg font-display font-bold text-slate-100">Custom integrations</h2>
        </div>
        <p className="text-sm text-slate-400 mb-5 max-w-3xl">Add any provider you want. This is a credential store + connectivity check — to make an integration actually DO work in production flows, connect it as an MCP server in MCP Connections.</p>
        {custom && custom.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="custom-grid">
            {custom.map((it) => <CustomCard key={it.id} item={it} onRefresh={load} />)}
          </div>
        ) : (
          <div className="bg-[#111827] border border-dashed border-slate-800 rounded-xl p-8 text-center" data-testid="custom-empty">
            <p className="text-sm text-slate-500">No custom integrations yet. Click <span className="text-violet-300">Add integration</span> to store credentials and run a real connectivity test.</p>
          </div>
        )}
      </div>
    </div>
  );
}
